import type { ReactNode } from "react"
import { ButtonBase, Tooltip, Typography } from "@mui/material"
import type { Theme } from "@mui/material"
import { makeStyles } from "tss-react/mui"

/** Colour of a segment in the "tinted" appearance; the other appearances ignore it. */
export type SegmentedControlTone = "primary" | "success" | "error"

export type SegmentedControlOption<T extends string> = Readonly<{
    value: T
    label: ReactNode
    badge?: string
    tooltip?: ReactNode
    tone?: SegmentedControlTone
}>

const TINTED_SEGMENT_COLORS: Record<SegmentedControlTone, (theme: Theme) => { fill: string; ink: string }> = {
    primary: (theme) => ({ fill: theme.palette.tailwind.sky[50], ink: theme.palette.primary.dark }),
    success: (theme) => ({ fill: theme.palette.tailwind.emerald[50], ink: theme.palette.success.dark }),
    error: (theme) => ({ fill: theme.palette.tailwind.red[50], ink: theme.palette.error.dark }),
}

export type SegmentedControlColor = "primary" | "purple" | "dark"

const ACTIVE_SEGMENT_COLORS: Record<SegmentedControlColor, (theme: Theme) => { fill: string; hoverFill: string }> = {
    primary: (theme) => ({ fill: theme.palette.primary.main, hoverFill: theme.palette.primary.dark }),
    purple: (theme) => ({ fill: theme.palette.tailwind.purple[600], hoverFill: theme.palette.tailwind.purple[700] }),
    dark: (theme) => ({ fill: theme.palette.tailwind.slate[800], hoverFill: theme.palette.tailwind.slate[900] }),
}

/** "medium" is the page-level control; "small" is the compact one used inside forms and cards. */
export type SegmentedControlSize = "medium" | "small"

/**
 * "segmented" paints the active segment solid in `color`; "track" is a white thumb on a grey track;
 * "tinted" gives the active segment a soft fill and a ring in the option's `tone`. `color` applies to "segmented" only.
 */
export type SegmentedControlAppearance = "segmented" | "track" | "tinted"

type Props<T extends string> = Readonly<{
    options: ReadonlyArray<SegmentedControlOption<T>>
    value: T | undefined
    onChange: (value: T) => void
    leadingLabel?: string
    color?: SegmentedControlColor
    size?: SegmentedControlSize
    appearance?: SegmentedControlAppearance
    fullWidth?: boolean
    className?: string
}>

type StyleProps = Readonly<{
    color: SegmentedControlColor
    size: SegmentedControlSize
    fullWidth: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { color, size, fullWidth }) => {
    const activeColors = ACTIVE_SEGMENT_COLORS[color](theme)
    const isSmall = size === "small"

    const tinted = (tone: SegmentedControlTone) => {
        const { fill, ink } = TINTED_SEGMENT_COLORS[tone](theme)
        return {
            backgroundColor: fill,
            color: ink,
            boxShadow: `inset 0 0 0 1px ${ink}`,
            "&:hover": {
                backgroundColor: fill,
            },
        }
    }

    return {
        container: {
            display: fullWidth ? "flex" : "inline-flex",
            alignItems: "stretch",
            boxSizing: "border-box",
            height: isSmall ? undefined : "3rem",
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${isSmall ? theme.palette.tailwind.gray[300] : theme.palette.tailwind.slate[300]}`,
            borderRadius: isSmall ? ".5rem" : ".75rem",
            overflow: "hidden",
        },
        segment: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flex: fullWidth ? 1 : "initial",
            padding: isSmall ? ".375rem 1rem" : ".75rem 1.25rem",
            fontFamily: "inherit",
            fontWeight: 600,
            fontSize: isSmall ? ".75rem" : ".875rem",
            color: isSmall ? theme.palette.tailwind.gray[600] : theme.palette.tailwind.slate[800],
            whiteSpace: "nowrap",
            // Doubled & beats the MuiButtonBase border reset, so the divider also shows on buttons.
            "&&:not(:first-of-type)": {
                borderLeft: `1px solid ${isSmall ? theme.palette.tailwind.gray[200] : theme.palette.tailwind.slate[300]}`,
            },
        },
        button: {
            transition: theme.transitions.create(["background-color", "color", "box-shadow"], {
                duration: theme.transitions.duration.short,
            }),
            "&:hover": {
                backgroundColor: theme.palette.action.hover,
            },
        },
        buttonActive: {
            backgroundColor: activeColors.fill,
            color: theme.palette.common.white,
            "&:hover": {
                backgroundColor: activeColors.hoverFill,
            },
        },
        badge: {
            marginLeft: ".5rem",
            padding: "0 .5rem",
            borderRadius: "1rem",
            fontWeight: 700,
            backgroundColor: theme.palette.tailwind.slate[200],
            color: theme.palette.tailwind.slate[700],
        },
        badgeActive: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.tailwind.slate[900],
        },
        trackContainer: {
            height: "auto",
            padding: ".1875rem",
            gap: ".1875rem",
            backgroundColor: theme.palette.tailwind.gray[100],
            border: `1px solid ${theme.palette.tailwind.gray[200]}`,
            borderRadius: ".625rem",
        },
        trackSegment: {
            padding: isSmall ? ".375rem .875rem" : ".5rem 1.25rem",
            fontWeight: 500,
            fontSize: ".875rem",
            color: theme.palette.tailwind.gray[600],
            borderRadius: ".4375rem",
            "&&:not(:first-of-type)": {
                borderLeft: "none",
            },
        },
        trackButton: {
            "&:hover": {
                backgroundColor: theme.palette.tailwind.gray[200],
            },
        },
        trackButtonActive: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.tailwind.gray[900],
            boxShadow: theme.shadows[1],
            "&:hover": {
                backgroundColor: theme.palette.common.white,
            },
        },
        trackBadge: {
            marginLeft: ".375rem",
            padding: 0,
            fontSize: "inherit",
            lineHeight: "inherit",
            fontWeight: 400,
            fontVariantNumeric: "tabular-nums",
            backgroundColor: "transparent",
            color: theme.palette.tailwind.gray[500],
        },
        trackBadgeActive: {
            color: theme.palette.tailwind.gray[600],
        },
        tintedContainer: {
            height: "2.5rem",
            borderColor: theme.palette.tailwind.slate[200],
            borderRadius: ".625rem",
        },
        tintedSegment: {
            fontWeight: 700,
            borderRadius: ".5rem",
            color: theme.palette.tailwind.slate[400],
            "&&:not(:first-of-type)": {
                borderLeft: 0,
            },
        },
        tintedPrimary: tinted("primary"),
        tintedSuccess: tinted("success"),
        tintedError: tinted("error"),
    }
})

export function StandardSegmentedControl<T extends string>({
    options,
    value,
    onChange,
    leadingLabel,
    color = "primary",
    size = "medium",
    appearance = "segmented",
    fullWidth = false,
    className,
}: Props<T>) {
    const { classes, cx } = useStyles({ color, size, fullWidth })
    const isTrack = appearance === "track"
    const isTinted = appearance === "tinted"

    const tintedActiveClasses: Record<SegmentedControlTone, string> = {
        primary: classes.tintedPrimary,
        success: classes.tintedSuccess,
        error: classes.tintedError,
    }

    return (
        <div
            className={cx(
                classes.container,
                isTrack && classes.trackContainer,
                isTinted && classes.tintedContainer,
                className,
            )}
        >
            {!!leadingLabel && (
                <span className={cx(classes.segment, isTrack && classes.trackSegment)}>{leadingLabel}</span>
            )}
            {options.map((option) => {
                const isActive = option.value === value
                const activeClasses: Record<SegmentedControlAppearance, string> = {
                    segmented: classes.buttonActive,
                    track: classes.trackButtonActive,
                    tinted: tintedActiveClasses[option.tone ?? "primary"],
                }

                const segment = (
                    <ButtonBase
                        key={option.value}
                        type="button"
                        aria-pressed={isActive}
                        className={cx(
                            classes.segment,
                            classes.button,
                            isTrack && classes.trackSegment,
                            isTinted && classes.tintedSegment,
                            isTrack && !isActive && classes.trackButton,
                            isActive && activeClasses[appearance],
                        )}
                        onClick={() => onChange(option.value)}
                    >
                        {option.label}
                        {option.badge !== undefined && (
                            <Typography
                                component="span"
                                variant="caption"
                                className={cx(
                                    classes.badge,
                                    isTrack && classes.trackBadge,
                                    isActive && (isTrack ? classes.trackBadgeActive : classes.badgeActive),
                                )}
                            >
                                {option.badge}
                            </Typography>
                        )}
                    </ButtonBase>
                )

                return option.tooltip ? (
                    <Tooltip key={option.value} title={option.tooltip}>
                        {segment}
                    </Tooltip>
                ) : (
                    segment
                )
            })}
        </div>
    )
}
